import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUsersApi } from '../api/api';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import TableTitle from '../components/TableTitle';
import { Table } from '../components/Table';
import Container from '../components/Container';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SearchBar from '../features/users/SearchBar';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Tooltip from '../components/Tooltip';
import { useRedirectToAccount } from '../features/user/useRedirectToAccount';

const LIMIT_USERS = 10;
const INITIAL_VALUES = { name: '', email: '', phone: '', id: '' };

const Users = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('id') || '';
  const [data, setData] = useState([]);
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT_USERS });
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState({ ...INITIAL_VALUES, id: userIdFromUrl });

  const { handleSwitchToUserAccount } = useRedirectToAccount();
  const fetchUsers = (initial = false) => {
    if (initial) setIsInitialLoading(true);
    else setIsLoading(true);

    setError(null);

    getUsersApi(
      page,
      limit,
      searchValue,
      (usersData) => {
        setData(usersData?.items || []);
        setTotalUsers(usersData?.totalUsers || 0);
        setIsLoading(false);
        setIsInitialLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    );
  };

  useEffect(() => {
    setSearchValue((prev) => (prev.id === userIdFromUrl ? prev : { ...prev, id: userIdFromUrl }));
  }, [userIdFromUrl]);

  useEffect(() => {
    fetchUsers(true);
  }, []);

  useEffect(() => {
    if (!isInitialLoading) {
      fetchUsers();
    }
  }, [page, limit, searchValue]);

  const totalPages = Math.ceil(totalUsers / limit);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value, id: '' }));
      if (searchParams.has('id')) {
        const next = new URLSearchParams(searchParams);
        next.delete('id');
        next.set('page', '1');
        setSearchParams(next);
        return;
      }
      updatePageUrl(1);
    },
    [updatePageUrl, searchParams, setSearchParams]
  );

  const inputsSearch = [
    {
      placeholder: 'Поиск по ФИО...',
      defaultValue: searchValue?.name,
      handleSearch: (value) => handleSearch(value, 'name'),
      prefix: <PersonIcon fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      placeholder: 'Поиск по Email...',
      defaultValue: searchValue?.email,
      handleSearch: (value) => handleSearch(value, 'email'),
      prefix: <EmailIcon fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      placeholder: 'Поиск по телефону...',
      defaultValue: searchValue?.phone,
      handleSearch: (value) => handleSearch(value, 'phone'),
      prefix: <PhoneIcon fontSize={'small'} className={'text-gray-500'} />,
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: 'fullname',
        title: 'ФИО',
      },
      {
        key: 'email',
        title: 'Email',
      },
      {
        key: 'is_foreign_email',
        title: 'Тип email',
        render: (value) => <div>{value ? 'Иностранный' : 'Локальный'}</div>,
      },
      {
        key: 'phone',
        title: 'Телефон',
        render: (value) => <div className={'min-w-max'}>{value ?? ' - '}</div>,
      },
      {
        key: 'created_at',
        title: 'Регистрация',
        render: (value) => new Date(value).toLocaleString('ru-RU'),
      },
      {
        key: 'last_login_at',
        title: 'Авторизация',
        render: (value) => new Date(value).toLocaleString('ru-RU'),
      },
      {
        key: 'object_count',
        title: 'Объекты',
      },
      {
        key: 'actions',
        title: 'Действия',
        render: (row) => (
          <div className={'text-center flex items-center justify-center'}>
            <Tooltip text="Перейти в ЛК">
              <button
                onClick={() => handleSwitchToUserAccount(row.id)}
                className="p-1 flex items-center justify-center rounded-full border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition">
                <ArrowForward />
              </button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleSwitchToUserAccount]
  );

  return (
    <Container>
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'Пользователи'} />
        <SearchBar inputs={inputsSearch} />
      </div>

      <Table columns={columns} data={data} loading={isLoading} error={error}>
        {!totalUsers || error ? null : <Pagination totalPages={totalPages} withLimit={true} />}
      </Table>
    </Container>
  );
};

export default Users;
